// src/pages/AdminPanel.js
import React, { useState, useEffect, useCallback } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import { useNotification } from '../contexts/NotificationContext';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';

// AdminList Component - 管理者一覧と追加フォーム
function AdminList() {
  const { signMessage } = useWeb3();
  const { showNotification } = useNotification();

  const [admins, setAdmins] = useState([]);
  const [newAdminAddress, setNewAdminAddress] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 管理者一覧取得
  const fetchAdmins = useCallback(async () => {
    try {
      setIsLoading(true);

      const message = `管理者一覧を取得します\n${Date.now()}`;
      const signature = await signMessage(message);

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/admins`, {
        headers: {
          'X-Signature': signature,
          'X-Message': message,
        },
      });

      setAdmins(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('管理者取得エラー:', error);
      showNotification('管理者一覧の取得に失敗しました', 'error');
      setIsLoading(false);
    }
  }, [signMessage, showNotification]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  // 新規管理者追加
  const handleAddAdmin = async e => {
    e.preventDefault();

    if (!newAdminAddress || !newAdminAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
      showNotification('有効なウォレットアドレスを入力してください', 'error');
      return;
    }

    try {
      setIsSubmitting(true);

      const message = `管理者を追加します\nアドレス: ${newAdminAddress}\n${Date.now()}`;
      const signature = await signMessage(message);

      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/admins/add`, {
        adminAddress: newAdminAddress,
        signature,
        message,
      });

      showNotification(`アドレス ${newAdminAddress} を管理者に追加しました`, 'success');
      setNewAdminAddress('');
      fetchAdmins();
    } catch (error) {
      console.error('管理者追加エラー:', error);
      showNotification(error.response?.data?.error || '管理者の追加に失敗しました', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 管理者削除
  const handleRemoveAdmin = async adminAddress => {
    if (!window.confirm(`${adminAddress} の管理者権限を削除しますか？`)) {
      return;
    }

    try {
      setIsSubmitting(true);

      const message = `管理者を削除します\nアドレス: ${adminAddress}\n${Date.now()}`;
      const signature = await signMessage(message);

      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/admins/remove`, {
        adminAddress,
        signature,
        message,
      });

      showNotification(`アドレス ${adminAddress} の管理者権限を削除しました`, 'success');
      fetchAdmins();
    } catch (error) {
      console.error('管理者削除エラー:', error);
      showNotification(error.response?.data?.error || '管理者の削除に失敗しました', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">管理者一覧</h2>

      {/* 管理者追加フォーム */}
      <form onSubmit={handleAddAdmin} className="mb-6">
        <h3 className="text-lg font-medium mb-2 text-gray-800 dark:text-gray-200">管理者追加</h3>
        <div className="flex">
          <input
            type="text"
            value={newAdminAddress}
            onChange={e => setNewAdminAddress(e.target.value)}
            placeholder="0x..."
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600"
          >
            {isSubmitting ? '処理中...' : '追加'}
          </button>
        </div>
      </form>

      {/* 管理者一覧表示 */}
      {isLoading ? (
        <div className="flex justify-center py-6">
          <LoadingSpinner size="sm" />
        </div>
      ) : (
        <div className="border rounded-md overflow-hidden">
          {admins.length > 0 ? (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {admins.map(admin => (
                <li key={admin.address} className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-gray-800 dark:text-gray-200 break-all">
                        {admin.address}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        追加した日時: {new Date(admin.createdAt).toLocaleString()}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveAdmin(admin.address)}
                      disabled={isSubmitting}
                      className="px-3 py-1 bg-red-600 text-sm text-white rounded hover:bg-red-700 disabled:bg-gray-300 dark:disabled:bg-gray-600"
                    >
                      削除
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              管理者が登録されていません
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// UserBanManagement Component - ユーザーBAN管理
function UserBanManagement() {
  const { signMessage } = useWeb3();
  const { showNotification } = useNotification();

  const [bannedUsers, setBannedUsers] = useState([]);
  const [banAddress, setBanAddress] = useState('');
  const [banReason, setBanReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // BAN済みユーザー一覧取得
  const fetchBannedUsers = useCallback(async () => {
    try {
      setIsLoading(true);

      const message = `BANユーザー一覧を取得します\n${Date.now()}`;
      const signature = await signMessage(message);

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/banned-users`, {
        headers: {
          'X-Signature': signature,
          'X-Message': message,
        },
      });

      setBannedUsers(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('BANユーザー取得エラー:', error);
      showNotification('BANユーザー一覧の取得に失敗しました', 'error');
      setIsLoading(false);
    }
  }, [signMessage, showNotification]);

  useEffect(() => {
    fetchBannedUsers();
  }, [fetchBannedUsers]);

  // ユーザーBAN処理
  const handleBanUser = async e => {
    e.preventDefault();

    if (!banAddress || !banAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
      showNotification('有効なウォレットアドレスを入力してください', 'error');
      return;
    }

    try {
      setIsSubmitting(true);

      const message = `ユーザーをBANします\nアドレス: ${banAddress}\n${Date.now()}`;
      const signature = await signMessage(message);

      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/users/ban`, {
        userAddress: banAddress,
        reason: banReason,
        signature,
        message,
      });

      showNotification(`アドレス ${banAddress} をBANしました`, 'success');
      setBanAddress('');
      setBanReason('');
      fetchBannedUsers();
    } catch (error) {
      console.error('ユーザーBAN処理エラー:', error);
      showNotification(error.response?.data?.error || 'ユーザーのBAN処理に失敗しました', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // BAN解除処理
  const handleUnbanUser = async userAddress => {
    if (!window.confirm(`${userAddress} のBANを解除しますか？`)) {
      return;
    }

    try {
      setIsSubmitting(true);

      const message = `ユーザーのBANを解除します\nアドレス: ${userAddress}\n${Date.now()}`;
      const signature = await signMessage(message);

      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/users/unban`, {
        userAddress,
        signature,
        message,
      });

      showNotification(`アドレス ${userAddress} のBANを解除しました`, 'success');
      fetchBannedUsers();
    } catch (error) {
      console.error('ユーザーBAN解除処理エラー:', error);
      showNotification(
        error.response?.data?.error || 'ユーザーのBAN解除処理に失敗しました',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">BANユーザー管理</h2>

      {/* ユーザーBAN処理フォーム */}
      <form onSubmit={handleBanUser} className="mb-6">
        <h3 className="text-lg font-medium mb-2 text-gray-800 dark:text-gray-200">ユーザーBAN</h3>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            BANするアドレス
          </label>
          <input
            type="text"
            value={banAddress}
            onChange={e => setBanAddress(e.target.value)}
            placeholder="0x..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            BAN理由
          </label>
          <textarea
            value={banReason}
            onChange={e => setBanReason(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="BANの理由（任意）"
            rows="3"
          />
        </div>

        <button
          type="submit"
          disabled={!banAddress || isSubmitting}
          className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 dark:disabled:bg-gray-600"
        >
          {isSubmitting ? '処理中...' : 'ユーザーをBANする'}
        </button>
      </form>

      {/* BANユーザー一覧表示 */}
      <div>
        <h3 className="text-lg font-medium mb-2 text-gray-800 dark:text-gray-200">
          BANされているユーザー
        </h3>

        {isLoading ? (
          <div className="flex justify-center py-6">
            <LoadingSpinner size="sm" />
          </div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            {bannedUsers.length > 0 ? (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {bannedUsers.map(user => (
                  <li key={user.address} className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-gray-800 dark:text-gray-200 break-all">
                          {user.address}
                        </div>
                        {user.banReason && (
                          <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            理由: {user.banReason}
                          </div>
                        )}
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          BANした日時: {new Date(user.bannedAt).toLocaleString()}
                        </div>
                      </div>
                      <button
                        onClick={() => handleUnbanUser(user.address)}
                        disabled={isSubmitting}
                        className="px-3 py-1 bg-blue-600 text-sm text-white rounded hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600"
                      >
                        BAN解除
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                BANされているユーザーはいません
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TokenManagement() {
  const { signMessage } = useWeb3();
  const { showNotification } = useNotification();

  const [tokens, setTokens] = useState([]);
  const [selectedToken, setSelectedToken] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fixed: Wrapped fetchTokens in useCallback to prevent recreating on every render
  const fetchTokens = useCallback(async () => {
    try {
      setIsLoading(true);

      const allTokensResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/tokens`);
      // grantTokenがある有効なトークンのみフィルタリング
      const validTokens = allTokensResponse.data.filter(token => token.grantToken);
      setTokens(validTokens);

      setIsLoading(false);
    } catch (error) {
      console.error('コイン取得エラー:', error);
      showNotification('コイン一覧の取得に失敗しました', 'error');
      setIsLoading(false);
    }
  }, [showNotification]); // Added showNotification as dependency

  // コイン一覧取得
  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]); // fetchTokens is now stable between renders due to useCallback

  // コインのBAN
  const handleBanToken = async () => {
    if (!selectedToken) {
      showNotification('コインを選択してください', 'error');
      return;
    }

    try {
      setIsSubmitting(true);

      const message = `コインをBANします\nコインID: ${selectedToken.id}\n${Date.now()}`;
      const signature = await signMessage(message);

      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/tokens/ban`, {
        tokenId: selectedToken.id,
        reason: banReason,
        signature,
        message,
      });

      showNotification(`コイン ${selectedToken.name} をBANしました`, 'success');
      setSelectedToken(null);
      setBanReason('');
      fetchTokens();
    } catch (error) {
      console.error('コインBAN処理エラー:', error);
      showNotification(error.response?.data?.error || 'コインのBAN処理に失敗しました', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // コインのBAN解除
  const handleUnbanToken = async token => {
    if (!token) return;

    if (!window.confirm(`コイン ${token.name} (${token.symbol}) のBANを解除しますか？`)) {
      return;
    }

    try {
      setIsSubmitting(true);

      const message = `コインのBANを解除します\nコインID: ${token.id}\n${Date.now()}`;
      const signature = await signMessage(message);

      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/tokens/unban`, {
        tokenId: token.id,
        signature,
        message,
      });

      showNotification(`コイン ${token.name} のBANを解除しました`, 'success');
      fetchTokens();
    } catch (error) {
      console.error('コインBAN解除処理エラー:', error);
      showNotification(error.response?.data?.error || 'コインのBAN解除処理に失敗しました', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">コイン管理</h2>

      <div className="mb-6">
        <h3 className="text-lg font-medium mb-2 text-gray-800 dark:text-gray-200">コインBAN</h3>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            BANするコイン
          </label>
          <select
            value={selectedToken ? selectedToken.id : ''}
            onChange={e => {
              const token = tokens.find(t => t.id === parseInt(e.target.value));
              setSelectedToken(token || null);
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">コインを選択...</option>
            {tokens
              .filter(token => !token.isBanned)
              .map(token => (
                <option key={token.id} value={token.id}>
                  {token.name} ({token.symbol})
                </option>
              ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            BAN理由
          </label>
          <textarea
            value={banReason}
            onChange={e => setBanReason(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="BANの理由（任意）"
            rows="3"
          />
        </div>

        <button
          onClick={handleBanToken}
          disabled={!selectedToken || isSubmitting}
          className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 dark:disabled:bg-gray-600"
        >
          {isSubmitting ? '処理中...' : 'コインをBANする'}
        </button>
      </div>

      <div>
        <h3 className="text-lg font-medium mb-2 text-gray-800 dark:text-gray-200">
          BANされているコイン
        </h3>

        {isLoading ? (
          <div className="flex justify-center py-6">
            <LoadingSpinner size="sm" />
          </div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            {tokens.filter(token => token.isBanned).length > 0 ? (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {tokens
                  .filter(token => token.isBanned)
                  .map(token => (
                    <li key={token.id} className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-gray-800 dark:text-gray-200">
                            {token.name} ({token.symbol})
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            アドレス: {token.launchToken || token.grantToken}
                          </div>
                          {token.bannedReason && (
                            <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                              理由: {token.bannedReason}
                            </div>
                          )}
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            BANした日時: {new Date(token.bannedAt).toLocaleString()}
                          </div>
                        </div>
                        <button
                          onClick={() => handleUnbanToken(token)}
                          disabled={isSubmitting}
                          className="px-3 py-1 bg-blue-600 text-sm text-white rounded hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600"
                        >
                          BAN解除
                        </button>
                      </div>
                    </li>
                  ))}
              </ul>
            ) : (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                BANされているコインはありません
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// MonitorManagement Component - モニター管理
function MonitorManagement() {
  const { signMessage } = useWeb3();
  const { showNotification } = useNotification();

  const [monitorStatus, setMonitorStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // モニター状態取得
  const fetchMonitorStatus = useCallback(async () => {
    try {
      setIsLoading(true);

      const message = `モニター状態を取得します\n${Date.now()}`;
      const signature = await signMessage(message);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/monitor/status`,
        {
          headers: {
            'X-Signature': signature,
            'X-Message': message,
          },
        }
      );

      setMonitorStatus(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('モニター状態取得エラー:', error);
      showNotification('モニター状態の取得に失敗しました', 'error');
      setIsLoading(false);
    }
  }, [signMessage, showNotification]);

  useEffect(() => {
    fetchMonitorStatus();
  }, [fetchMonitorStatus]);

  // モニターのON/OFF切り替え
  const toggleMonitor = async () => {
    if (!monitorStatus) return;

    const newStatus = !monitorStatus.isRunning;
    const actionText = newStatus ? '開始' : '停止';

    if (!window.confirm(`モニターを${actionText}しますか？`)) {
      return;
    }

    try {
      setIsSubmitting(true);

      const message = `モニターを${actionText}します\n${Date.now()}`;
      const signature = await signMessage(message);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/monitor/${newStatus ? 'start' : 'stop'}`,
        {
          signature,
          message,
        }
      );

      showNotification(`モニターを${actionText}しました`, 'success');
      fetchMonitorStatus();
    } catch (error) {
      console.error(`モニター${actionText}エラー:`, error);
      showNotification(
        error.response?.data?.error || `モニターの${actionText}に失敗しました`,
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">モニター管理</h2>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <LoadingSpinner size="sm" />
        </div>
      ) : monitorStatus ? (
        <div>
          <div className="mb-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div>
                <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
                  モニター状態
                </h3>
                <div className="mt-1 flex items-center">
                  <div
                    className={`w-3 h-3 rounded-full mr-2 ${
                      monitorStatus.isRunning ? 'bg-green-500' : 'bg-red-500'
                    }`}
                  ></div>
                  <span className="text-gray-600 dark:text-gray-300">
                    {monitorStatus.isRunning ? '実行中' : '停止中'}
                  </span>
                </div>
                {monitorStatus.lastExecutedAt && (
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    最終実行: {new Date(monitorStatus.lastExecutedAt).toLocaleString()}
                  </div>
                )}
              </div>
              <button
                onClick={toggleMonitor}
                disabled={isSubmitting}
                className={`px-4 py-2 text-white rounded-md ${
                  monitorStatus.isRunning
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                } disabled:bg-gray-300 dark:disabled:bg-gray-600`}
              >
                {isSubmitting
                  ? '処理中...'
                  : monitorStatus.isRunning
                  ? 'モニターを停止'
                  : 'モニターを開始'}
              </button>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2 text-gray-800 dark:text-gray-200">
              モニター設定
            </h3>
            <div className="border rounded-md p-4 bg-gray-50 dark:bg-gray-700">
              <div className="mb-4">
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  実行間隔:
                </span>
                <span className="text-gray-700 dark:text-gray-300">
                  {monitorStatus.intervalMinutes
                    ? `${monitorStatus.intervalMinutes}分ごと`
                    : '設定なし'}
                </span>
              </div>

              <div>
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  監視対象:
                </span>
                {monitorStatus.monitorTargets && monitorStatus.monitorTargets.length > 0 ? (
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
                    {monitorStatus.monitorTargets.map((target, index) => (
                      <li key={index}>{target}</li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-gray-500 dark:text-gray-400">設定なし</span>
                )}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-2 text-gray-800 dark:text-gray-200">
              モニターログ
            </h3>
            {monitorStatus.logs && monitorStatus.logs.length > 0 ? (
              <div className="border rounded-md overflow-hidden">
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {monitorStatus.logs.map((log, index) => (
                    <li key={index} className="p-3">
                      <div className="text-sm">
                        <div className="font-medium text-gray-800 dark:text-gray-200">
                          {log.message}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(log.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="p-4 text-center border rounded-md text-gray-500 dark:text-gray-400">
                ログはありません
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
          モニター情報を取得できませんでした
        </div>
      )}
    </div>
  );
}

// AdminPanel のメイン部分
function AdminPanel() {
  const { account, isAdmin } = useWeb3();
  const [activeTab, setActiveTab] = useState('admin'); // 'admin', 'users', 'tokens', 'monitor'
  const [isLoading, setIsLoading] = useState(true);

  // Fixed: Changed to a regular variable since we're not updating it
  const error = '';

  // Fixed: Added a simple useEffect to set loading state to false
  useEffect(() => {
    // Simply update loading state to false - since we're not making any API calls in this component anymore
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 max-w-4xl mx-auto">
        <div className="text-center py-6">
          <div className="text-red-600 dark:text-red-400 text-xl font-bold mb-2">{error}</div>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-4xl mx-auto text-center">
        <h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">管理者パネル</h1>
        <p className="mb-6 text-gray-700 dark:text-gray-300">
          管理機能を利用するには、ウォレットを接続してください。
        </p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-4xl mx-auto text-center">
        <h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">管理者パネル</h1>
        <p className="mb-6 text-gray-700 dark:text-gray-300">
          このページにアクセスする権限がありません。管理者のみ利用できます。
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">管理者パネル</h1>

      {/* タブナビゲーション */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('admin')}
            className={`flex-1 py-3 px-4 text-center font-medium text-sm focus:outline-none ${
              activeTab === 'admin'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            管理者
          </button>

          <button
            onClick={() => setActiveTab('users')}
            className={`flex-1 py-3 px-4 text-center font-medium text-sm focus:outline-none ${
              activeTab === 'users'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            ユーザー管理
          </button>

          <button
            onClick={() => setActiveTab('tokens')}
            className={`flex-1 py-3 px-4 text-center font-medium text-sm focus:outline-none ${
              activeTab === 'tokens'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            コイン管理
          </button>

          <button
            onClick={() => setActiveTab('monitor')}
            className={`flex-1 py-3 px-4 text-center font-medium text-sm focus:outline-none ${
              activeTab === 'monitor'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            モニター
          </button>
        </div>

        {/* タブコンテンツ */}
        <div className="p-6">
          {activeTab === 'xxx_admin' && <AdminList />}
          {activeTab === 'xxx_users' && <UserBanManagement />}
          {activeTab === 'xxx_tokens' && <TokenManagement />}
          {activeTab === 'xxx_monitor' && <MonitorManagement />}
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
